<template>
  <div class="product-tile" :id="id" tabindex="0" aria-live="polite" :aria-label="product.title">
    <div class="product-tile__badge-container">
      <span
        class="product-tile__badge badge"
        v-if="productBadge"
        :style="badgeStyles"
      >
        {{ productBadge }}

      </span>
    </div>
    <div class="product-tile__images">
      <button
        v-if="$mq === 'mobile'"
        title="Previous Product Image"
        class="product-tile__mobile-image-button product-tile__mobile-image-button--prev"
        @click="mobileImagesSwapped = !mobileImagesSwapped">
        <icon name="chevron-left" size="30"></icon>
      </button>
      <div class="product-tile__images-wrapper">
        <a :href="url" aria-live="polite" :aria-label="product.title">
          <img
            v-if="images.default && !pulled"
            class="product-tile__img-main"
            v-lazy="imageSize(images.default, '304x')"
            :alt="images.defaultAlt"
            :data-srcset="`${imageSize(images.default, '168x')} 168w, ${imageSize(images.default, '304x')} 304w, ${imageSize(images.default, '372x')} 372w, ${imageSize(images.default, '456x')} 1.5x`"
          />
          <img
            v-else-if="images.default"
            class="product-tile__img-main product-tile__img-main--pulled"
            :src="imageSize(images.default, '304x')"
            :srcset="images.defaultSrcset"
            :sizes="sizes"
            :alt="images.defaultAlt"
          />
          <img
            v-if="images.hover && !pulled"
            class="product-tile__img-hover"
            :class="{ 'product-tile__mobile-hovered' : mobileImagesSwapped }"
            v-lazy="imageSize(images.hover, '304x')"
            :alt="images.hoverAlt"
            :data-srcset="`${imageSize(images.hover, '168x')} 168w, ${imageSize(images.hover, '304x')} 304w, ${imageSize(images.default, '372x')} 372w, ${imageSize(images.hover, '456x')} 1.5x`"
          />
          <img
            v-else-if="images.hover"
            class="product-tile__img-hover"
            :class="{
              'product-tile__mobile-hovered': mobileImagesSwapped,
              'product-tile__img-hover--pulled': pulled
            }"
            :src="imageSize(images.hover, '304x')"
            :srcset="images.hoverSrcset"
            :sizes="sizes"
            :alt="images.hoverAlt"
          />
        </a>
      </div>

    <div class="product-tile--quickshop" v-if="$mq !== 'mobile' && collection === true">
        <button class="margin-top product-tile__atqs button--full button button--secondary" @click="quickShop()">Quick Shop</button>
    </div>
    <product-featured-badges v-if="product.featuredBadges" :badges="product.featuredBadges" />
      <button
        v-if="$mq === 'mobile'"
        title="Next Product Image"
        class="product-tile__mobile-image-button product-tile__mobile-image-button--next"
        @click="mobileImagesSwapped = !mobileImagesSwapped">
        <icon name="chevron-right" size="30"></icon>
      </button>
    </div>
    <a :href="url" aria-live="polite" :aria-label="product.title">
      <div class="product-tile__static-text-content text-left" :class="[collection ? 'product-tile__static-text-content--collection': '']">
        <div class="row">
          <div class="col-12-sm">
            <span class="pftm__review-stars-container">
               <icon name="review-star" size="15px" v-for='(n,index) in yotpoRating' :key="index"></icon>
               <icon name="review-half-star" size="15px"  v-if="yotpoRatingHalf==true"></icon>
            </span>
          </div>
          <div class="col-12-sm">
            <span class="product-tile-vendor-name">{{ product.vendor }}</span>
          </div>
          <div class="col-12-sm product-tile__title">
            <span class="product-tile-title body-sm link link--secondary"> {{ staticProductName }}</span>
          </div>
          <div class="col-12-sm product-tile__type color-grey" v-if="product.product_fit || product.product_type">
            <span class="product-tile-type body-sm">{{ staticProductType }}</span>
          </div>
        </div>
        <div class="row">
          <div class="product-tile__price-container" v-if="!product.hidePrice">
            <span class="product-tile__price product-tile__compare-price" :class="{'color-util-sale' : onSale}">
              {{ variant.price | moneyWithoutDecimals }}
            </span>
            <span class="product-tile__price color-black" v-if="onSale">
              <s>{{ variant.compare_at_price | moneyWithoutDecimals }}</s>
            </span>
          </div>
          <div v-if="$mq !== 'mobile' && collection && product.collection_product_description" class="col-12-sm product-tile__description" v-html="product.collection_product_description"></div>

        </div>
      </div>
    </a>
  </div>

</template>

<script>
import ProductSwatchesSimple from './ProductSwatchesSimple.vue'
import ProductReviewsBadge from '@/scripts/components/product/ProductReviewsBadge.vue'
import { imageSize, imageCrop, badge, getProductImageAlt, getProductImageHover } from '@/scripts/helpers/util'
import { withId } from '@/scripts/helpers/props'
import uniqBy from 'lodash/uniqBy'
import Icon from '@/scripts/components/basic/Icon.vue';
import ProductFeaturedBadges from './ProductFeaturedBadges.vue';
import { mapState } from 'vuex'
const YOTPO_ID = 'zloja1agC5m6ys5KYTXaSfjyFMn54oio8xcBpwRr';
/**
   * @typedef ImageSource
   * @type {Object}
   * @prop {String} default
   * @prop {String} defaultAlt
   * @prop {String} hoverAlt
   *
   * @typedef Buttons
   * @type {Object}
   * @prop {Boolean} showOptions
   * @prop {Boolean} showAtc
   */

export default {
  name: 'ProductTileSimple',
  data () {
    return {
      variant: this.selectedVariant || this.product.selected_or_first_available_variant || this.product.variants[0],
      loaded: false,
      yotpoRating: 0,
      yotpoRatingHalf: false,
      mobileImagesSwapped: false,
      sizes: '(max-width: 500px) 200px, (max-width: 650px) 450px, (max-width: 992px) 350px, (max-width: 1400px) 450px, (max-width: 1800px) 600px, (max-width: 2559px) 900px, (min-width: 2560px) 950px',
    }
  },
  components: {
    ProductReviewsBadge,
    Icon,
    ProductFeaturedBadges
  },
  props: {
    pulled: {
      type: Boolean,
      default: false,
    },
    product: Object,
    onAddToCart: Function, // if not provided, the atc will not show
    collectionHandle: String, // used for linking to pdp with proper collection
    textAlignment: String,
    settings: Object,
    forceMobile: { // force mobile text size on desktop widths
      default: false,
    },
    truncateTitle: {
      type: Boolean,
      default: true
    },
    fullTileClick: {
      type: Boolean,
      default: true
    },
    collection: {
      type: Boolean,
      default: false
    },
    badgesColors:{
      type: Object,
      default: () => ({
        backgroundColor:"",
        textColor: ""
      })
    },
    ...withId()
  },
  filters: {
    truncated (s) {
      if (s.length > 42) { return `${s.substring(0, 42)}...` }
      return s
    }
  },
  watch: {
    selectedVariant (n, o) {
      if (n.id !== o.id) {
        this.variant = n
      }
    }
  },
  mounted () {

    fetch(`https://api.yotpo.com/products/${YOTPO_ID}/${this.product.id}/bottomline`)
        .then((blob) => blob.json())
        .then((data) => {

          this.yotpoRatingHalf = ((data.response.bottomline.average_score%1).toFixed()>"0.5")
          this.yotpoRating = parseInt(data.response.bottomline.average_score);

        });
  },
  computed: {
    ...mapState({
      badgesColorsDynamic: state => state.badgeColorMap
    }),
    selectOptionsVerbiage () {
      const { available } = this.variant
      return available ? 'More Options' : 'Out of Stock'
    },

    /** @returns {String} */
    url () {
      const urlParts = []

      if (!this.product.handle) {
        if (this.product.url) return this.product.url;
      }

      if (this.collectionHandle) { urlParts.push(`/collections/${this.collectionHandle}`) }

      urlParts.push(`/products/${this.product.handle}`)

      // only add variant if we're not the default
      if (this.product.variants.map(v => v.id)[0] !== this.variant.id) { urlParts.push(`?variant=${this.variant.id}`) }

      return urlParts.join('')
    },

    /** @returns {ImageSource} */
    images() {
      const defaultImage = this.product.images[0];
      const defaultImageAlt = this.product.title;
      const nextImage = this.product.images[1] ? this.product.images[1] : this.product.images[0];
      const nextImageAlt = this.product.title;
      let defaultSrcset, hoverSrcset;

      if (this.pulled) {
        let sizes = ['200', '350', '450', '600', '780', '900', '950', '1200', '1400', '1600'];
        [defaultSrcset, hoverSrcset] = sizes.reduce(
          (both, size, index) => {
            const isLast = index === sizes.length - 1;
            both[0].push(this.generateSrcset(defaultImage.src, size, isLast));
            both[1].push(this.generateSrcset(nextImage.src, size, isLast));
            return both;
          },
          [[], []]
        );
      }

      return {
        default: defaultImage ? defaultImage.src : window.bvaccel.noImage,
        defaultAlt: defaultImageAlt,
        defaultSrcset,
        hover: nextImage ? nextImage.src : window.bvaccel.noImage,
        hoverAlt: nextImageAlt,
        hoverSrcset,
      };
    },

    /** @returns {Buttons} */
    buttons () {
      return {
        showOptions: this.product.magnetProductHandle || (!this.hasColorVariants && this.filteredVariants.length > 0),
        showAtc: !this.product.magnetProductHandle && this.variant.available && this.filteredVariants.length < 1
      }
    },

    /** @returns {Number} */
    colorIndex () {
      if (!this.hasColorVariants) return 0

      return this.product.options.reduce((acc, opt, i) => /color/i.test(opt) ? i : acc, 0)
    },

    /** @returns {String} */
    colorsAvailable () {
      if (!this.hasColorVariants) return
      const { colorIndex } = this

      const colors = new Set(this.product.variants.map(variant => variant[`option${colorIndex + 1}`]))

      if (colors.size >= 1) {
        return `${colors.size} color${colors.size > 1 ? 's' : ''} available`
      }
    },

    /** @returns {Array<Object>} */
    filteredVariants () {
      return this.product.variants.filter(v => v.title.toLowerCase() !== 'default title')
    },

    filteredColorVariants () {
      if (!this.hasColorVariants) return []

      return uniqBy(this.product.variants, `option${this.colorIndex + 1}`)
    },

    /** @returns {Boolean} */
    onSale () {
      return this.variant.available && this.variant.compare_at_price && this.variant.price < this.variant.compare_at_price
    },

    /** @returns {String} */
    productBadge () {
      return badge(this.product, this.onSale);
    },

    badgeStyles() {
      if (this.onSale && this.productBadge != 'new') {
        return {
          'background-color': this.badgesColorsDynamic["sale"][1],
          'color': this.badgesColorsDynamic["sale"][0],
          'border-radius': '20px'
        }
      } else {

        return {
          'background-color': this.badgesColorsDynamic[this.productBadge][1],
          'color': this.badgesColorsDynamic[this.productBadge][0],
          'border-radius': '20px'
        }

        // if(!this.badgesColors.textColor) {
        //   return {
        //     'background-color': '#757660'
        //   }
        // }else{
        //   return {
        //     'background-color': this.badgesColors.backgroundColor,
        //     'color': this.badgesColors.textColor
        //   }
        // }
      }
    },
    /** @returns {String} */
    staticProductName() {
      return this.product.title.split('-').pop().trim()
    },

    staticProductType() {
      if (this.product.product_fit) return this.product.product_fit;

      return this.product.product_type;
    }

  },
  methods: {
    imageSize,
    imageCrop,

    generateSrcset(image, size, isLast) {
      return `${imageSize(image, size + 'x')} ${size}w${isLast ? '' : ','}`;
    },

    quickShop() {
      let handle=this.product.handle;
      const payload={
        product: handle
      };
      this.$store.dispatch('quickshop/setQuickShop', payload);
    },
  }
}
</script>

<style scoped lang="scss">

.pftm__review-stars-container {
  display: flex;
  justify-content: left;
  height: 15px;
}

.badge {
  color: #ffffff;
  background-color: rgb(51, 50, 50);;

}

.product-tile {
  position: relative;
  user-select: none;
  display: flex;
  flex-direction: column;
  color: inherit;
  cursor: pointer;
  height: auto;
  a {
    text-decoration: none;
  }

  &__compare-price {
    margin-right: 5px;
  }

  @include mobile-only {
    height: 275px; // each carousel box height
  }

  @include ie {
    display: block;
  }

  /* Fixes iOS :hover issue where it ignores click */
  @media (hover: hover) {
    &:hover &__img-hover,
    &:focus &__img-hover {
      opacity: 1;
    }
  }

  .row > * {
    margin-bottom: 0;
  }

  .button-row {
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 0 8px;
    @include mobile-tablet {
      .button__content {
        font-size: 12px;
      }
    }
  }

  &__price {
    @include product-tile-price;
    font-family: $font-family-primary;
    font-size: 18px;
    letter-spacing: 0;
    line-height: 22px;
    text-align: center;
    @include mobile-only {
      font-size: 13px;
      line-height: 15px;
    }
  }

  &__description {
    color: $color-black-600;
    font-size: rem(11);
    line-height: rem(15);
    letter-spacing: 0;
    text-align: left;
    margin-bottom: 0;

    /deep/ strong {
      font-weight: normal;
    }

    .collection-grid-columns--1 & {
      font-size: rem(12);
      line-height: rem(16);

      span {
        margin: auto;
        max-width: 260px;
        display: block;
      }
    }

    @include tablet-up {
      font-size: rem(12);
      line-height: rem(16);
    }
  }

  @media screen and (max-width: 400px) {
    max-width: 100%;
  }

  a {
    color: inherit;
  }

  &-title {
    font-size: 17px;
    line-height: 25px;
    color: $color-text-soft-black;
    font-weight: 500;
    white-space: normal;
    @include mobile-only {
      font-size: 15px;
      line-height: 17px;
    }
  }

  &-color {
    font-size: rem(12);
    line-height: 1em !important;
  }

  &__images {
    position: relative;
    font-size: 0;
    height: 200px;
    @include tablet-up {
      height: 300px; // image file size in the each carousel box.
    }
    width: 100%;
    background-color: $color-white;
  }

  &__images .product-tile__images-wrapper {
    font-size: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  &__img-hover {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    transition: opacity 0.2s linear;
  }

  &__img-main {
    z-index: 2;
    position: relative;
    transition: none;
    transition: opacity 0.2s linear;
  }

  &__img-hover {
    z-index: 5;
  }

  &__img-main--placeholder {
    width: 100%;
    height: 100%;
    z-index: 4;
    fill: $color-lightest-grey;
  }

  &__img-main,
  &__img-hover {
    width: 100%;
    height: 100%;
    min-width: 100%;
    min-height: 100%;
    object-fit: cover;

    &--pulled {
      max-width: none;
      top: -15px;
      left: -15px;
      width: calc(100% + 30px);
      height: calc(100% + 30px);


      @include tablet-up {
        top: -25px;
        left: -25px;
        width: calc(100% + 50px);
        height: calc(100% + 50px);
      }
    }
  }

  &__static-text-content {
    z-index: 2;
    width: 100%;
    position: static;
    bottom: auto;
    margin-top: 11px;
    padding-left: 10px;
  }

  &__static-text-content--collection {
    position: static;
    bottom: auto;
    margin-top: 11px;
  }

  &__img-main--placeholder {
    width: 100%;
    height: 100%;
  }

  &--quickshop {
    position: absolute;
    bottom: 0;
    z-index: 99;
    width: 100%;
    padding:20px;
    display: none;
  }

  &__atqs {
    font-family: Optima;
    font-size: 18px;
    letter-spacing: 0;
    line-height: 20px;
    text-align: center;
    display: block;
    line-height: 44px;
    background: $color-green-slate-200;
  }

}

.product-tile-vendor-name {
  color: $color-black-700;
  font-size: 14px;
  margin-top:10px;
  line-height: 1.33;
  white-space: normal;
}

.product-tile:hover .product-tile--quickshop {
  display:block;
}
.glossary-products {
  /deep/ .product-tile__price {
    font-size: 15px;
  }
  /deep/ a{
    text-decoration: none;
  }
}
.text-left {
  text-align: left;
}
.color-util-sale {
  color: $color-util-sale !important;
}
</style>
